// International SEO utilities for Super User Manager
import { Locale } from '../../i18n-config';

// Geographic targeting data for enhanced localization
export const GEOGRAPHIC_TARGETING = {
  CH: { primary: 'en-CH', secondary: 'fr-CH', currency: 'CHF', timezone: 'Europe/Zurich' },
  FR: { primary: 'fr-FR', secondary: 'en-US', currency: 'EUR', timezone: 'Europe/Paris' },
  BE: { primary: 'fr-FR', secondary: 'en-US', currency: 'EUR', timezone: 'Europe/Brussels' },
  LU: { primary: 'fr-FR', secondary: 'en-US', currency: 'EUR', timezone: 'Europe/Luxembourg' },
  MC: { primary: 'fr-FR', secondary: 'en-US', currency: 'EUR', timezone: 'Europe/Monaco' },
  US: { primary: 'en-US', secondary: 'fr-FR', currency: 'USD', timezone: 'America/New_York' },
  CA: { primary: 'en-US', secondary: 'fr-FR', currency: 'CAD', timezone: 'America/Toronto' },
  GB: { primary: 'en-US', secondary: 'fr-FR', currency: 'GBP', timezone: 'Europe/London' },
  AU: { primary: 'en-US', secondary: 'fr-FR', currency: 'AUD', timezone: 'Australia/Sydney' },
} as const;

// Language codes mapping for hreflang
export const HREFLANG_CODES = {
  'en-US': 'en-US',
  'fr-FR': 'fr-FR',
  'en-CH': 'en-CH',
  'fr-CH': 'fr-CH',
} as const;

// Regional market data for targeted content
export const MARKET_DATA = {
  'en-US': {
    marketName: 'North America',
    currency: 'USD',
    phoneFormat: '+1 xxx-xxx-xxxx',
    businessHours: '9 AM - 5 PM EST',
    supportEmail: 'support-us@super-user-manager.com',
    timezone: 'America/New_York',
    dateFormat: 'MM/dd/yyyy',
    numberFormat: 'en-US',
  },
  'fr-FR': {
    marketName: 'France',
    currency: 'EUR',
    phoneFormat: '+33 x xx xx xx xx',
    businessHours: '9h - 17h CET',
    supportEmail: 'support-fr@super-user-manager.com',
    timezone: 'Europe/Paris',
    dateFormat: 'dd/MM/yyyy',
    numberFormat: 'fr-FR',
  },
  'en-CH': {
    marketName: 'Switzerland',
    currency: 'CHF',
    phoneFormat: '+41 xx xxx xx xx',
    businessHours: '9 AM - 5 PM CET',
    supportEmail: 'support-ch@super-user-manager.com',
    timezone: 'Europe/Zurich',
    dateFormat: 'dd.MM.yyyy',
    numberFormat: 'de-CH',
  },
  'fr-CH': {
    marketName: 'Suisse',
    currency: 'CHF',
    phoneFormat: '+41 xx xxx xx xx',
    businessHours: '9h - 17h CET',
    supportEmail: 'support-ch@super-user-manager.com',
    timezone: 'Europe/Zurich',
    dateFormat: 'dd.MM.yyyy',
    numberFormat: 'fr-CH',
  },
} as const;

// High-purchasing-power markets for global areaServed (Schema.org compliant)
export const GLOBAL_AREA_SERVED = [
  // North America
  { '@type': 'Country', name: 'United States' },
  { '@type': 'Country', name: 'Canada' },

  // Western Europe (core market)
  { '@type': 'Country', name: 'United Kingdom' },
  { '@type': 'Country', name: 'Germany' },
  { '@type': 'Country', name: 'France' },
  { '@type': 'Country', name: 'Switzerland' },
  { '@type': 'Country', name: 'Netherlands' },
  { '@type': 'Country', name: 'Belgium' },
  { '@type': 'Country', name: 'Austria' },
  { '@type': 'Country', name: 'Sweden' },
  { '@type': 'Country', name: 'Norway' },
  { '@type': 'Country', name: 'Denmark' },
  { '@type': 'Country', name: 'Ireland' },
  { '@type': 'Country', name: 'Finland' },
  { '@type': 'Country', name: 'Luxembourg' },
  { '@type': 'Country', name: 'Italy' },
  { '@type': 'Country', name: 'Spain' },

  // Asia-Pacific
  { '@type': 'Country', name: 'Japan' },
  { '@type': 'Country', name: 'Singapore' },
  { '@type': 'Country', name: 'South Korea' },
  { '@type': 'Country', name: 'Australia' },
  { '@type': 'Country', name: 'New Zealand' },

  // Middle East
  { '@type': 'Country', name: 'United Arab Emirates' },
] as const;
// Generate comprehensive hreflang link tags
export function generateHreflangLinks(
  currentPath: string,
  baseUrl: string = 'https://super-user-manager.com'
): Array<{
  rel: string;
  hreflang: string;
  href: string;
}> {
  const hreflangLinks: Array<{ rel: string; hreflang: string; href: string }> = [];

  // Add links for all supported locales
  Object.entries(HREFLANG_CODES).forEach(([locale, hreflangCode]) => {
    const localeSegment = locale === 'en-US' ? '' : `/${locale}`;
    const href = `${baseUrl}${localeSegment}${currentPath}`;

    hreflangLinks.push({
      rel: 'alternate',
      hreflang: hreflangCode,
      href,
    });
  });

  // Add x-default for international users
  hreflangLinks.push({
    rel: 'alternate',
    hreflang: 'x-default',
    href: `${baseUrl}${currentPath}`,
  });

  return hreflangLinks;
}

// Generate geographic targeting meta tags
export function generateGeoTargetingTags(locale: Locale): { [key: string]: string } {
  const marketData = MARKET_DATA[locale];

  // Extract country code from locale
  const countryCode = locale.includes('CH') ? 'CH' : locale.includes('FR') ? 'FR' : 'US';

  const geoData = GEOGRAPHIC_TARGETING[countryCode as keyof typeof GEOGRAPHIC_TARGETING];

  return {
    'geo.region': countryCode,
    'geo.placename': marketData.marketName,
    ICBM: getCoordinatesForCountry(countryCode),
    'geo.position': getCoordinatesForCountry(countryCode),
    currency: marketData.currency,
    'content-language': locale,
    language: locale.split('-')[0],
  };
}

// Helper function to get coordinates for major markets
function getCoordinatesForCountry(countryCode: string): string {
  const coordinates = {
    CH: '46.8182, 8.2275', // Bern, Switzerland
    FR: '48.8566, 2.3522', // Paris, France
    US: '40.7128, -74.0060', // New York, USA
  };

  return coordinates[countryCode as keyof typeof coordinates] || coordinates.US;
}

// Generate localized business information schema
export function generateLocalBusinessSchema(locale: Locale): object {
  const marketData = MARKET_DATA[locale];
  const countryCode = locale.includes('CH') ? 'CH' : locale.includes('FR') ? 'FR' : 'US';

  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Super User Manager',
    description: locale.startsWith('fr')
      ? 'Plateforme leader de gestion clientèle pour PME avec programmes de fidélité intégrés'
      : 'Leading customer management platform for SMEs with integrated loyalty programs',
    address: {
      '@type': 'PostalAddress',
      addressCountry: countryCode,
      addressRegion: marketData.marketName,
    },
    telephone: '+41 78 314 55 98', // Primary Swiss number
    email: marketData.supportEmail,
    url: 'https://super-user-manager.com',
    currenciesAccepted: [marketData.currency],
    paymentAccepted: ['Credit Card', 'Bank Transfer', 'PayPal'],
    priceRange: locale.includes('CH') ? 'Free - CHF 280' : locale.includes('FR') ? 'Gratuit - 280€' : 'Free - $280',
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '17:00',
      timeZone: marketData.timezone,
    },
    areaServed: GLOBAL_AREA_SERVED,
    knowsLanguage: locale.startsWith('fr') ? ['French', 'English'] : ['English', 'French'],
  };
}

// Generate localized contact information
export function getLocalizedContactInfo(locale: Locale): {
  phone: string;
  phoneFormatted: string;
  email: string;
  businessHours: string;
  timezone: string;
  whatsapp: string;
  supportLanguages: string[];
} {
  const marketData = MARKET_DATA[locale];

  return {
    phone: '+41783145598',
    phoneFormatted: '+41 78 314 55 98',
    email: marketData.supportEmail,
    businessHours: marketData.businessHours,
    timezone: marketData.timezone,
    whatsapp: '+41783145598',
    supportLanguages: locale.startsWith('fr') ? ['Français', 'English', 'Deutsch'] : ['English', 'French', 'German'],
  };
}

// Generate canonical URL with proper locale handling
export function generateCanonicalURL(
  path: string,
  locale: Locale,
  baseUrl: string = 'https://super-user-manager.com'
): string {
  const localeSegment = locale === 'en-US' ? '' : `/${locale}`;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;

  return `${baseUrl}${localeSegment}${cleanPath}`;
}

// Generate localized sitemap URLs for international SEO
export function generateLocalizedSitemapUrls(baseUrl: string = 'https://super-user-manager.com'): Array<{
  url: string;
  locale: Locale;
  hreflang: string;
  market: string;
}> {
  const urls: Array<{ url: string; locale: Locale; hreflang: string; market: string }> = [];

  Object.entries(HREFLANG_CODES).forEach(([locale, hreflangCode]) => {
    const localeSegment = locale === 'en-US' ? '' : `/${locale}`;
    const marketData = MARKET_DATA[locale as Locale];

    urls.push({
      url: `${baseUrl}${localeSegment}`,
      locale: locale as Locale,
      hreflang: hreflangCode,
      market: marketData.marketName,
    });
  });

  return urls;
}

// Validate locale-specific URLs for SEO compliance
export function validateInternationalSEO(
  locale: Locale,
  path: string
): {
  isValid: boolean;
  issues: string[];
  recommendations: string[];
} {
  const issues: string[] = [];
  const recommendations: string[] = [];

  // Check if locale is properly formatted
  if (!Object.keys(HREFLANG_CODES).includes(locale)) {
    issues.push(`Invalid locale: ${locale}`);
  }

  // Check path format
  if (!path.startsWith('/')) {
    issues.push('Path should start with forward slash');
  }

  // Market-specific validations
  const marketData = MARKET_DATA[locale];
  if (!marketData) {
    issues.push(`No market data available for locale: ${locale}`);
  }

  // Generate recommendations
  if (issues.length === 0) {
    recommendations.push('Consider adding hreflang tags for all alternate languages');
    recommendations.push('Ensure content is properly localized for target market');
    recommendations.push('Add geo-targeting meta tags for better local SEO');
    recommendations.push('Include local business schema markup');
  }

  return {
    isValid: issues.length === 0,
    issues,
    recommendations,
  };
}

const internationalSEO = {
  GEOGRAPHIC_TARGETING,
  HREFLANG_CODES,
  MARKET_DATA,
  generateHreflangLinks,
  generateGeoTargetingTags,
  generateLocalBusinessSchema,
  getLocalizedContactInfo,
  generateCanonicalURL,
  generateLocalizedSitemapUrls,
  validateInternationalSEO,
};

export default internationalSEO;
